/**
 * Pill primitive — ikke-interaktiv etikett / statuschip (#152).
 *
 * Handoffens statuschip har fire toner (MAPPING.md §3):
 *   - warn     `bg-warn-bg text-warn`   venter, frister, allergier, blokkert
 *   - ok       `bg-ok-bg text-good`     godkjent, aktiv
 *   - lime     `bg-lime-bg text-lime`   poeng, streak, fremheving
 *   - neutral  `bg-track text-ink`      alt annet
 * pluss `solid` (`bg-solid text-on-solid`) for en høykontrast-etikett,
 * `bad` (`bg-bad text-sheet`) for det kritiske, og kategoritonene `blue` og
 * `lilac` (belønningskategoriene og frysningene på Premier, #156).
 *
 * Interaktive filterchips er `Chip`, ikke denne.
 */
import type { ReactNode } from 'react';

export type PillTone = 'warn' | 'ok' | 'lime' | 'neutral';
export type PillVariant =
  | PillTone
  | 'blue'
  | 'lilac'
  | 'solid'
  | 'bad';

/** Delt med StatusPill, FlameChip og PointChip — de fire tonene på ett sted. */
export const TONE_CLASS: Record<PillTone, string> = {
  warn: 'bg-warn-bg text-warn',
  ok: 'bg-ok-bg text-good',
  lime: 'bg-lime-bg text-lime',
  neutral: 'bg-track text-ink',
};

const VARIANT_CLASS: Record<PillVariant, string> = {
  ...TONE_CLASS,
  blue: 'bg-blue-bg text-blue',
  lilac: 'bg-lilac-bg text-lilac',
  solid: 'bg-solid text-on-solid',
  bad: 'bg-bad text-sheet',
};

interface PillProps {
  variant?: PillVariant;
  children: ReactNode;
  className?: string;
}

export function Pill({ variant = 'neutral', children, className }: PillProps) {
  return (
    <span
      className={[
        'inline-flex min-h-7 items-center gap-1 rounded-pill px-2.5 py-1 text-xs font-extrabold',
        VARIANT_CLASS[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </span>
  );
}
